import type { AppProps } from 'next/app';
import { ThemeProvider } from 'styled-components';
import { ZHLPageHead, ZHLTheme } from '@zillow/zhl-page-structure';
import dynamic from 'next/dynamic';
import { UniversalServerSideProps } from 'src/utils/getters/getUniversalPageProps/utils/getAllUniversalProps';
import { UniversalWrappers } from 'src/providers/UniversalWrappers';
import { ErrorBoundary } from 'src/components/ErrorBoundary';
import { PossibleZillowSession } from 'src/services/auth/types';
import { FunctionComponentWithChildren } from 'src/utils/types';
import { isServerSide } from 'src/utils/validators/isServerSide';
import { GlobalStyle } from 'src/common/styles/GlobalStyle';
import { deferredDatadog } from 'src/services/datadog/deferred';

// These errors are noisy and don't provide any value because we intentionally know better
const KNOWN_NOISY_ERROR_SNIPPETS = [
    'Invalid prop `marginBottom`',
    'Invalid prop `gap`',
    'Invalid prop `marginX`',
    'Invalid prop `alignItems` supplied to `Flex`',
    'for a non-boolean attribute',
    'Warning: useLayoutEffect ',
];

const UNIVERSAL_ANALYTICS_SDK_VALIDATION_INDICATOR = 'Web SDK Validation';
/* eslint-disable */
const actualError = console.error;

console.error = (...data) => {
    const isUniversalAnalyticsSdkNoise = (data: string[]) =>
        data.some(
            (message: string | string[]) =>
                typeof message === 'string' && message.includes(UNIVERSAL_ANALYTICS_SDK_VALIDATION_INDICATOR)
        );

    const isKnownNoisyError = (data: string[]) =>
        data.some((message: string) => KNOWN_NOISY_ERROR_SNIPPETS.some(snippet => `${message}`.includes(snippet)));
    if (Array.isArray(data)) {
        if (isUniversalAnalyticsSdkNoise(data)) {
            console.info('Suppressed Universal Analytics SDK noise');
            return;
        }
        if (isKnownNoisyError(data)) {
            console.info('Suppressed invalid noisy prop notification');
            return;
        }
    }
    actualError(...data);
};
/* eslint-enable */

const PageCannotLoad = dynamic(async () => {
    const { WiredPageCannotLoad: Component } = await import(
        /* webpackChunkName: "PageCannotLoad" */
        '../experiences/Error/PageCannotLoad/PageCannotLoad.wired'
    );
    return Component;
});

const MortgageApplicationCenterApp = ({ Component, pageProps }: AppProps) => {
    const {
        session,
        experiments,
        experimentClientInitialized,
        resolvedTrackers,
        requestedExperiments,
    }: {
        session: PossibleZillowSession;
    } & Partial<UniversalServerSideProps> = pageProps;
    const WiredUniversalWrappers: FunctionComponentWithChildren = ({ children }) => (
        <UniversalWrappers
            experiments={experiments ?? {}}
            experimentClientInitialized={!!experimentClientInitialized}
            resolvedTrackers={resolvedTrackers ?? []}
            requestedExperiments={requestedExperiments ?? []}
            session={session}>
            {children}
        </UniversalWrappers>
    );
    return (
        <>
            <ZHLPageHead currentDomain={isServerSide() ? '' : window.location.origin} />
            <ThemeProvider theme={ZHLTheme}>
                <GlobalStyle />
                <ErrorBoundary
                    onError={(error, context) => {
                        deferredDatadog.addError(error, context);
                    }}
                    fallback={
                        <WiredUniversalWrappers>
                            <PageCannotLoad />
                        </WiredUniversalWrappers>
                    }>
                    <WiredUniversalWrappers>
                        <Component {...pageProps} />
                    </WiredUniversalWrappers>
                </ErrorBoundary>
            </ThemeProvider>
        </>
    );
};

export default MortgageApplicationCenterApp;
